import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  Cpu as CpuIcon,
  FileText as FileTextIcon,
  Radio as RadioIcon,
  Shield as ShieldIcon
} from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { decodeRouteParam } from '@atbash-labs/plantview-links'
import {
  collectAdditionalProperties,
  useGetPlantFunctionLogicDiagramDetailQuery,
  type PlantProtectiveFunction,
  type PlantSafetyPoint
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import {
  plantLogicSolverHref,
  plantProtectiveFunctionHref,
  plantSafetyIndexHref,
  plantSafetyPointHref
} from '../../lib/plant-links'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { SectionHeader } from './entity/SectionHeader'
import { PlantHref } from './PlantHref'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table'

/**
 * `/plant/safety/flds/:fldId` — Function Logic Diagram detail.
 *
 * Lists Safety Manager points on this FLD and SRS functions that
 * `IMPLEMENTED_BY` it.
 */
const FunctionLogicDiagramDetailPage = (): React.ReactElement => {
  const { fldId = '' } = useParams<{ fldId: string }>()
  const decodedFldId = decodeRouteParam(fldId)
  const navigate = useNavigate()
  const { data, isLoading, isError } = useGetPlantFunctionLogicDiagramDetailQuery(decodedFldId, {
    skip: !decodedFldId
  })

  const diagram = data?.diagram
  const points = React.useMemo(() => data?.points ?? [], [data?.points])
  const functions = React.useMemo(() => data?.functions ?? [], [data?.functions])
  const additionalProperties = React.useMemo(
    () =>
      diagram
        ? collectAdditionalProperties(
            'FunctionLogicDiagram',
            diagram as unknown as Record<string, unknown>
          )
        : [],
    [diagram]
  )

  return (
    <div className="flex flex-1 flex-col gap-card">
      <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
        <Link to={plantSafetyIndexHref()} className="hover:text-ink hover:underline">
          Safety
        </Link>
        {data?.solver && (
          <>
            <span className="mx-1.5 text-ink-4">/</span>
            <Link
              to={plantLogicSolverHref(data.solver.id)}
              className="hover:text-ink hover:underline"
            >
              {data.solver.name}
            </Link>
          </>
        )}
        <span className="mx-1.5 text-ink-4">/</span>
        <span className="text-ink">{diagram?.name ?? decodedFldId}</span>
      </nav>

      {isError ? (
        <DetailFallback
          title="Couldn't load this function logic diagram"
          hint="The FLD may have been deleted or the proxy is unreachable."
        />
      ) : isLoading || !data ? (
        <DetailFallback title="Loading function logic diagram…" hint="" muted />
      ) : (
        <>
          <header className="flex items-start gap-4 rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
            <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
              <FileTextIcon size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h1 className="m-0 min-w-0 break-words text-2xl font-semibold text-ink">
                  {diagram?.name ?? decodedFldId}
                </h1>
                {diagram?.fld_number && <Badge variant="secondary">FLD {diagram.fld_number}</Badge>}
                {diagram?.application && <Badge variant="outline">{diagram.application}</Badge>}
              </div>
              <p className="m-0 mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-ink-3">
                {data.solver && (
                  <>
                    <CpuIcon size={14} aria-hidden />
                    <Link
                      to={plantLogicSolverHref(data.solver.id)}
                      className="hover:text-ink hover:underline"
                    >
                      {data.solver.name}
                    </Link>
                  </>
                )}
                {data.site && (
                  <>
                    <span className="text-ink-4">·</span>
                    <PlantHref
                      id={data.site.id}
                      label="Site"
                      node={data.site}
                      className="hover:text-ink hover:underline"
                    >
                      {data.site.name}
                    </PlantHref>
                  </>
                )}
              </p>
              {diagram?.description && (
                <p className="m-0 mt-3 text-sm text-ink-2">{diagram.description}</p>
              )}
            </div>
          </header>

          <PointsSection points={points} onOpen={(id) => navigate(plantSafetyPointHref(id))} />
          <ImplementedBySection functions={functions} />

          {diagram && (
            <EntityDetailsInspector entity={diagram} additionalProperties={additionalProperties} />
          )}
        </>
      )}
    </div>
  )
}

function PointsSection({
  points,
  onOpen
}: {
  points: PlantSafetyPoint[]
  onOpen: (id: string) => void
}): React.ReactElement {
  return (
    <section>
      <SectionHeader
        label="Safety points"
        subtitle={`${points.length} ${points.length === 1 ? 'point' : 'points'}`}
      />
      {points.length === 0 ? (
        <EmptyCard title="No points" hint="This FLD has no contained SafetyPoints." />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tag</TableHead>
                <TableHead className="w-[12%]">Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[14%]">Flags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {points.map((point) => (
                <TableRow
                  key={point.id}
                  className="cursor-pointer"
                  onClick={() => onOpen(point.id)}
                >
                  <TableCell className="font-medium">
                    <Link
                      to={plantSafetyPointHref(point.id)}
                      className="text-ink no-underline hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {point.name}
                    </Link>
                  </TableCell>
                  <TableCell>{point.point_type ?? <span className="text-ink-4">—</span>}</TableCell>
                  <TableCell className="truncate text-ink-2">
                    {point.description ?? <span className="text-ink-4">—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {point.safety_related && <Badge variant="outline">Safety</Badge>}
                      {point.is_internal && <Badge variant="secondary">Internal</Badge>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  )
}

function ImplementedBySection({
  functions
}: {
  functions: PlantProtectiveFunction[]
}): React.ReactElement {
  return (
    <section>
      <SectionHeader
        label="Implements"
        subtitle={`${functions.length} ${functions.length === 1 ? 'function' : 'functions'}`}
      />
      {functions.length === 0 ? (
        <EmptyCard
          title="No linked protective functions"
          hint="Run the Safety Manager ↔ SRS mapping job to link matching SRS protective functions to this FLD."
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          {functions.map((fn) => (
            <Link
              key={fn.id}
              to={plantProtectiveFunctionHref(fn.id)}
              className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand-soft px-2 py-0.5 text-11 font-medium text-brand no-underline hover:bg-brand-soft/70"
            >
              <ShieldIcon size={11} aria-hidden />
              {fn.name}
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}

function EmptyCard({ title, hint }: { title: string; hint: string }): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      <Empty title={title} hint={hint} icon={RadioIcon} />
    </div>
  )
}

function DetailFallback({
  title,
  hint,
  muted
}: {
  title: string
  hint: string
  muted?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      {muted ? (
        <div className="text-sm text-ink-3">{title}</div>
      ) : (
        <Empty title={title} hint={hint} icon={WarningCircleIcon} />
      )}
    </div>
  )
}

export default FunctionLogicDiagramDetailPage
